import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Collapse,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Add,
  PhotoCamera,
  CheckCircle,
  Cancel,
  Edit,
  Delete,
  Home,
  NavigateNext,
  Visibility,
  ExpandMore,
  ExpandLess,
  Elevator,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE } from '../api';
import { getLojaNome } from '../utils/lojas';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeBr, formatHorario } from '../utils/datetime';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'OK', label: 'OK - Funcionando', color: 'success' },
  { value: 'OFF', label: 'OFF - Desligado', color: 'error' },
];

export default function TestesElevadores() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isManutencao =
    (user?.nivel_acesso || '').toLowerCase() === 'manutenção' ||
    (user?.nivel_acesso || '').toLowerCase() === 'manutencao';

  const [testes, setTestes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    clienteId: '',
    status: '',
    busca: '',
  });

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  // Dialogo novo/editar
  const [openDialog, setOpenDialog] = useState(false);
  const [editandoTeste, setEditandoTeste] = useState(null);
  const [novoTeste, setNovoTeste] = useState({
    data_teste: new Date().toISOString().split('T')[0],
    cliente_id: '',
    horario: '',
    foto: null,
    video: null,
    status: 'OK',
    observacao: '',
  });

  // Detalhes
  const [openDetalhesDialog, setOpenDetalhesDialog] = useState(false);
  const [testeDetalhes, setTesteDetalhes] = useState(null);

  // Imagem ampliada
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  // Expandido
  const [expandedRow, setExpandedRow] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const getHorarioAtual = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  useEffect(() => {
    carregarTestes();
    carregarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abrir detalhes via query param
  useEffect(() => {
    try {
      const qp = new URLSearchParams(location.search);
      const id = qp.get('id');
      if (id) {
        const found = testes.find((t) => String(t.id) === String(id));
        if (found) {
          setTesteDetalhes(found);
          setOpenDetalhesDialog(true);
          navigate(location.pathname, { replace: true });
        } else {
          (async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${API_BASE}/testes-elevador/${id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
              });
              if (res.ok) {
                const data = await res.json();
                setTesteDetalhes(data);
                setOpenDetalhesDialog(true);
                navigate(location.pathname, { replace: true });
              }
            } catch (e) {
              /* ignore */
            }
          })();
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [location.search, location.pathname, navigate, testes]);

  const carregarTestes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/testes-elevador/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (response.ok) {
        setTestes(await response.json());
      }
    } catch (error) {
      console.error('Erro ao carregar testes:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API_BASE}/me/clientes`, {
          headers,
        });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        /* ignore */
      }
      const response = await fetch(`${API_BASE}/clientes/`, { headers });
      if (response.ok) {
        let data = await response.json();
        if (clienteIds && clienteIds.length > 0) {
          data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
        }
        setClientes(data);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const getClienteNome = (clienteId) => getLojaNome(clienteId, clientes);

  const filtrarTestes = () => {
    let resultado = [...testes];
    if (filtros.dataInicio) {
      resultado = resultado.filter((t) => t.data_teste >= filtros.dataInicio);
    }
    if (filtros.dataFim) {
      resultado = resultado.filter((t) => t.data_teste <= filtros.dataFim);
    }
    if (filtros.clienteId) {
      resultado = resultado.filter((t) => t.cliente_id === parseInt(filtros.clienteId));
    }
    if (filtros.status) {
      resultado = resultado.filter((t) => t.status === filtros.status);
    }
    if (filtros.busca) {
      const b = filtros.busca.toLowerCase();
      resultado = resultado.filter(
        (t) =>
          getClienteNome(t.cliente_id).toLowerCase().includes(b) ||
          (t.observacao || '').toLowerCase().includes(b) ||
          String(t.id).includes(b)
      );
    }
    resultado.sort((a, b) =>
      (b.data_teste + (b.horario || '')).localeCompare(a.data_teste + (a.horario || ''))
    );
    return resultado;
  };

  const testesFiltrados = filtrarTestes();
  const paginatedTestes = testesFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // CRUD handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      novoTeste.status === 'OFF' &&
      (!novoTeste.observacao || novoTeste.observacao.trim().length === 0)
    ) {
      setSnackbar({
        open: true,
        message: 'Observação é obrigatória quando o status for OFF',
        severity: 'error',
      });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('data_teste', novoTeste.data_teste);
      formData.append('cliente_id', novoTeste.cliente_id);
      formData.append('horario', novoTeste.horario);
      formData.append('status', novoTeste.status);
      if (novoTeste.observacao) formData.append('observacao', novoTeste.observacao);
      if (novoTeste.foto) formData.append('foto', novoTeste.foto);
      if (novoTeste.video) formData.append('video', novoTeste.video);

      const url = editandoTeste
        ? `${API_BASE}/testes-elevador/${editandoTeste.id}`
        : `${API_BASE}/testes-elevador/`;
      const method = editandoTeste ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        method,
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (response.ok) {
        setSnackbar({
          open: true,
          message: editandoTeste ? 'Teste atualizado!' : 'Teste salvo!',
          severity: 'success',
        });
        fecharDialogo();
        carregarTestes();
      } else {
        const errorData = await response.text();
        try {
          const errorJson = JSON.parse(errorData);
          if (
            response.status === 401 &&
            (errorJson?.detail || '').includes('Could not validate credentials')
          ) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setSnackbar({
              open: true,
              message: 'Sua sessão expirou. Faça login novamente.',
              severity: 'warning',
            });
            setTimeout(() => (window.location.href = '/login'), 1200);
            return;
          }
        } catch (_) {
          /* ignore */
        }
        throw new Error(editandoTeste ? 'Erro ao atualizar teste' : 'Erro ao salvar teste');
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const deletarTeste = async (testeId) => {
    if (!window.confirm('Tem certeza que deseja deletar este teste?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/testes-elevador/${testeId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Teste deletado!',
          severity: 'success',
        });
        carregarTestes();
      } else {
        throw new Error('Erro ao deletar teste');
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const abrirDialogoNovoTeste = () => {
    setEditandoTeste(null);
    setNovoTeste({
      data_teste: new Date().toISOString().split('T')[0],
      cliente_id: '',
      horario: getHorarioAtual(),
      foto: null,
      video: null,
      status: 'OK',
      observacao: '',
    });
    setOpenDialog(true);
  };

  const abrirDialogoEdicao = (teste) => {
    setEditandoTeste(teste);
    setNovoTeste({
      data_teste: teste.data_teste,
      cliente_id: teste.cliente_id,
      horario: teste.horario,
      foto: null,
      video: null,
      status: teste.status,
      observacao: teste.observacao || '',
    });
    setOpenDialog(true);
  };

  const fecharDialogo = () => {
    setOpenDialog(false);
    setEditandoTeste(null);
    setNovoTeste({
      data_teste: new Date().toISOString().split('T')[0],
      cliente_id: '',
      horario: getHorarioAtual(),
      foto: null,
      video: null,
      status: 'OK',
      observacao: '',
    });
  };

  const handleFotoChange = (event) => {
    const file = event.target.files[0];
    if (file) setNovoTeste((prev) => ({ ...prev, foto: file }));
  };

  const handleVideoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'O arquivo de vídeo não pode exceder 10MB',
          severity: 'error',
        });
        return;
      }
      setNovoTeste((prev) => ({ ...prev, video: file }));
    }
  };

  const limparFiltros = () => {
    setFiltros({
      dataInicio: '',
      dataFim: '',
      clienteId: '',
      status: '',
      busca: '',
    });
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" /> Dashboard
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/testes-loja-menu')}
        >
          Testes de Loja
        </Link>
        <Typography color="text.primary">Testes de Elevadores</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Elevator sx={{ fontSize: 32, color: '#7B1FA2' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Testes de Elevadores
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={abrirDialogoNovoTeste}
          sx={{
            backgroundColor: '#7B1FA2',
            '&:hover': { backgroundColor: '#6A1B9A' },
          }}
        >
          Novo Teste
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Cliente"
              value={filtros.clienteId}
              onChange={(e) =>
                setFiltros((prev) => ({
                  ...prev,
                  clienteId: e.target.value,
                }))
              }
              fullWidth
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              {clientes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {getClienteNome(c.id)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Status"
              value={filtros.status}
              onChange={(e) =>
                setFiltros((prev) => ({
                  ...prev,
                  status: e.target.value,
                }))
              }
              fullWidth
              size="small"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Buscar"
              value={filtros.busca}
              onChange={(e) => setFiltros((prev) => ({ ...prev, busca: e.target.value }))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="date"
              label="Data Início"
              value={filtros.dataInicio}
              onChange={(e) =>
                setFiltros((prev) => ({
                  ...prev,
                  dataInicio: e.target.value,
                }))
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="date"
              label="Data Fim"
              value={filtros.dataFim}
              onChange={(e) =>
                setFiltros((prev) => ({
                  ...prev,
                  dataFim: e.target.value,
                }))
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setPage(0)}
              sx={{ height: 40, backgroundColor: '#1976d2' }}
            >
              PESQUISAR
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Subfiltro de Status */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Filtrar por Status:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={filtros.status === opt.value ? 'contained' : 'outlined'}
              size="small"
              color={opt.color || 'primary'}
              onClick={() => {
                setFiltros((prev) => ({ ...prev, status: opt.value }));
                setPage(0);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: filtros.status === opt.value ? 600 : 400,
              }}
            >
              {opt.label}
            </Button>
          ))}
          {(filtros.dataInicio ||
            filtros.dataFim ||
            filtros.clienteId ||
            filtros.status ||
            filtros.busca) && (
            <Button
              size="small"
              onClick={limparFiltros}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Limpar filtros
            </Button>
          )}
        </Box>
      </Box>

      {/* Contagem */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {testesFiltrados.length} teste(s) encontrado(s)
      </Typography>

      {/* Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : testesFiltrados.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nenhum teste encontrado</Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedTestes.map((teste) => (
              <Grid item xs={12} sm={6} md={3} key={teste.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    {/* Cabeçalho */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        Teste #{teste.id}
                      </Typography>
                      <Chip
                        icon={teste.status === 'OK' ? <CheckCircle /> : <Cancel />}
                        label={teste.status}
                        color={teste.status === 'OK' ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {'📅 '}
                      {formatDateTimeBr(teste.data_teste, formatHorario(teste.horario))}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {'👤 '}
                      {getClienteNome(teste.cliente_id)}
                    </Typography>

                    {/* Chips de mídia */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      {teste.foto && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label="1 foto"
                          icon={<PhotoCamera />}
                        />
                      )}
                      {teste.video && (
                        <Chip size="small" variant="outlined" color="error" label="1 vídeo" />
                      )}
                    </Box>

                    {/* Observação */}
                    {teste.observacao && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ mt: 1, fontWeight: 500 }}
                      >
                        {'📋 '}
                        {teste.observacao.length > 80
                          ? teste.observacao.substring(0, 80) + '...'
                          : teste.observacao}
                      </Typography>
                    )}

                    {teste.criado_em && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        {'🕐 Criado: '}
                        {formatDateTimeBr(teste.criado_em)}
                      </Typography>
                    )}

                    {/* Ações */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTesteDetalhes(teste);
                          setOpenDetalhesDialog(true);
                        }}
                        title="Visualizar"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirDialogoEdicao(teste);
                        }}
                        title="Editar"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {!isManutencao && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            deletarTeste(teste.id);
                          }}
                          title="Deletar"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => setExpandedRow(expandedRow === teste.id ? null : teste.id)}
                        title="Ver detalhes"
                      >
                        {expandedRow === teste.id ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </CardContent>

                  {/* Detalhe expandido */}
                  <Collapse in={expandedRow === teste.id} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        px: 2,
                        pb: 2,
                        bgcolor: 'grey.50',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {teste.observacao && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Observação completa:
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {teste.observacao}
                          </Typography>
                        </Box>
                      )}
                      {teste.foto && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Foto:
                          </Typography>
                          <Box
                            component="img"
                            src={`/uploads/testes-elevador/${teste.foto}`}
                            alt="Foto do teste"
                            sx={{
                              maxWidth: '100%',
                              maxHeight: 200,
                              borderRadius: 1,
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setSelectedImage(`/uploads/testes-elevador/${teste.foto}`);
                              setOpenImageDialog(true);
                            }}
                          />
                        </Box>
                      )}
                      {teste.video && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Vídeo:
                          </Typography>
                          <video controls style={{ maxWidth: '100%', maxHeight: 200 }}>
                            <source src={`/uploads/testes-elevador/${teste.video}`} />
                          </video>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Card>
              </Grid>
            ))}
          </Grid>

          <TablePagination
            component="div"
            count={testesFiltrados.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[8, 12, 24, 48]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}

      {/* Dialog Novo/Editar */}
      <Dialog open={openDialog} onClose={fecharDialogo} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editandoTeste ? `Editar Teste #${editandoTeste.id}` : 'Novo Teste de Elevador'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box
                sx={{
                  flex: '1 1 220px',
                  minWidth: { xs: '100%', sm: 220 },
                }}
              >
                <TextField
                  type="date"
                  label="Data do Teste"
                  value={novoTeste.data_teste}
                  onChange={(e) =>
                    setNovoTeste((prev) => ({
                      ...prev,
                      data_teste: e.target.value,
                    }))
                  }
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box
                sx={{
                  flex: '1 1 180px',
                  minWidth: { xs: '100%', sm: 180 },
                }}
              >
                <TextField
                  type="time"
                  label="Horário"
                  value={novoTeste.horario}
                  onChange={(e) =>
                    setNovoTeste((prev) => ({
                      ...prev,
                      horario: e.target.value,
                    }))
                  }
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box
                sx={{
                  flex: '1 1 260px',
                  minWidth: { xs: '100%', sm: 240 },
                }}
              >
                <TextField
                  select
                  fullWidth
                  required
                  label="Cliente"
                  value={String(novoTeste.cliente_id || '')}
                  onChange={(e) =>
                    setNovoTeste((prev) => ({
                      ...prev,
                      cliente_id: Number(e.target.value),
                    }))
                  }
                >
                  <MenuItem value="" disabled>
                    Selecione o cliente
                  </MenuItem>
                  {clientes.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {getClienteNome(c.id)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box
                sx={{
                  flex: '1 1 260px',
                  minWidth: { xs: '100%', sm: 240 },
                }}
              >
                <TextField
                  select
                  fullWidth
                  required
                  label="Status"
                  value={novoTeste.status}
                  onChange={(e) =>
                    setNovoTeste((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="OK">OK - Funcionamento Correto</MenuItem>
                  <MenuItem value="OFF">OFF - Desligado</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                <TextField
                  label={
                    novoTeste.status === 'OFF'
                      ? 'Observação (Obrigatória)'
                      : 'Observação (Opcional)'
                  }
                  value={novoTeste.observacao}
                  onChange={(e) =>
                    setNovoTeste((prev) => ({
                      ...prev,
                      observacao: e.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 150 }}
                  helperText={`${novoTeste.observacao.length}/150 caracteres`}
                  required={novoTeste.status === 'OFF'}
                  placeholder={
                    novoTeste.status === 'OK'
                      ? 'Adicione uma observação se necessário...'
                      : 'Descreva o problema encontrado...'
                  }
                />
              </Box>
              <Box
                sx={{
                  flex: '1 1 260px',
                  minWidth: { xs: '100%', sm: 260 },
                }}
              >
                <Button variant="outlined" component="label" startIcon={<PhotoCamera />} fullWidth>
                  {novoTeste.foto ? novoTeste.foto.name : 'Adicionar Foto (Opcional)'}
                  <input type="file" hidden accept="image/*" onChange={handleFotoChange} />
                </Button>
                {novoTeste.foto && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 0.5, color: 'success.main' }}
                  >
                    ✓ Foto selecionada: {novoTeste.foto.name}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  flex: '1 1 260px',
                  minWidth: { xs: '100%', sm: 260 },
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  color="secondary"
                >
                  {novoTeste.video ? novoTeste.video.name : 'Adicionar Vídeo (Opcional - Max 10MB)'}
                  <input type="file" hidden accept="video/*" onChange={handleVideoChange} />
                </Button>
                {novoTeste.video && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 0.5, color: 'success.main' }}
                  >
                    ✓ Vídeo selecionado: {novoTeste.video.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharDialogo}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: '#7B1FA2',
                '&:hover': { backgroundColor: '#6A1B9A' },
              }}
            >
              {editandoTeste ? 'Atualizar Teste' : 'Salvar Teste'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Detalhes */}
      <Dialog
        open={openDetalhesDialog}
        onClose={() => {
          setTesteDetalhes(null);
          setOpenDetalhesDialog(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Detalhes do Teste #{testeDetalhes?.id}
            </Typography>
            <Chip
              icon={testeDetalhes?.status === 'OK' ? <CheckCircle /> : <Cancel />}
              label={
                testeDetalhes?.status === 'OK' ? 'OK - Funcionamento Correto' : 'OFF - Desligado'
              }
              color={testeDetalhes?.status === 'OK' ? 'success' : 'error'}
              size="medium"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {testeDetalhes && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    📋 Informações do Teste
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data do Teste
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTimeBr(
                        testeDetalhes.data_teste,
                        formatHorario(testeDetalhes.horario)
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Horário
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatHorario(testeDetalhes.horario)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cliente
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getClienteNome(testeDetalhes.cliente_id)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {testeDetalhes.status === 'OK' ? 'Funcionamento Correto' : 'Desligado'}
                    </Typography>
                  </Box>
                  {testeDetalhes.observacao && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Observações
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          mt: 1,
                          backgroundColor: testeDetalhes.status === 'OFF' ? '#ffebee' : '#e8f5e8',
                          border: `1px solid ${
                            testeDetalhes.status === 'OFF' ? '#ffcdd2' : '#c8e6c8'
                          }`,
                        }}
                      >
                        <Typography variant="body2">{testeDetalhes.observacao}</Typography>
                      </Paper>
                    </Box>
                  )}
                  {testeDetalhes.criado_em && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Criado em
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTimeBr(testeDetalhes.criado_em)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    🎬 Arquivos de Mídia
                  </Typography>
                  {testeDetalhes.foto_url || testeDetalhes.foto ? (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        📸 Foto do Teste
                      </Typography>
                      <Box
                        component="img"
                        src={
                          testeDetalhes.foto_url || `/uploads/testes-elevador/${testeDetalhes.foto}`
                        }
                        alt="Foto do teste"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: '2px solid #e0e0e0',
                        }}
                        onClick={() => {
                          setSelectedImage(
                            testeDetalhes.foto_url ||
                              `/uploads/testes-elevador/${testeDetalhes.foto}`
                          );
                          setOpenImageDialog(true);
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Nenhuma foto disponível
                    </Typography>
                  )}
                  {testeDetalhes.video_url || testeDetalhes.video ? (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        🎥 Vídeo do Teste
                      </Typography>
                      <video
                        controls
                        style={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          borderRadius: 4,
                        }}
                      >
                        <source
                          src={
                            testeDetalhes.video_url ||
                            `/uploads/testes-elevador/${testeDetalhes.video}`
                          }
                        />
                      </video>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum vídeo disponível
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTesteDetalhes(null);
              setOpenDetalhesDialog(false);
            }}
          >
            Fechar
          </Button>
          {testeDetalhes && (
            <Button
              variant="contained"
              onClick={() => {
                setOpenDetalhesDialog(false);
                abrirDialogoEdicao(testeDetalhes);
              }}
            >
              Editar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Imagem Ampliada */}
      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="img"
            src={selectedImage}
            alt="Imagem ampliada"
            sx={{ maxWidth: '100%', maxHeight: '90vh' }}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
